import { useEffect, useRef, useState } from 'preact/hooks'
import { ChevronDown, Search, Check } from 'lucide-preact'
import { assetApi } from '@/api/asset'
import type { WebAssetListItemDto } from '@/api/asset'
import { lowerSafe } from '@/utils/strings'

const EMAIL_COPY_TYPE = 'Email Copy'
const DRAFT_STATUS = 'Draft'
const SIGNED_OFF_STATUS = 'Signed Off'

interface EmailAssetSelectProps {
  clientCompanyId: string
  workspaceId: string
  campaignId: string
  webId: string
  selectedAssetId: string | null
  /** Asset ids already bound to other email touchpoints on this Web — tagged "Used" in the list. */
  usedAssetIds?: ReadonlySet<string>
  onSelect: (asset: WebAssetListItemDto) => void
  onClear: () => void
  onNavigateToAssets?: () => void
}

/** Green once signed off (ready to send), amber while it's still in the sign-off workflow. */
function StatusDot({ status }: { status: string }) {
  return (
    <span
      className={'inline-block w-2 h-2 rounded-full shrink-0 ' + (status === SIGNED_OFF_STATUS ? 'bg-emerald-500' : 'bg-amber-500')}
      aria-hidden="true"
    />
  )
}

/**
 * Email-copy picker for the activation editor. A searchable dropdown (vs. a chip row) so it scales
 * to many emails per Web, while keeping the at-a-glance status dot. Shows non-Draft Email Copy
 * assets, Signed Off first.
 */
export function EmailAssetSelect({
  clientCompanyId,
  workspaceId,
  campaignId,
  webId,
  selectedAssetId,
  usedAssetIds,
  onSelect,
  onClear,
  onNavigateToAssets,
}: EmailAssetSelectProps) {
  const [assets, setAssets] = useState<WebAssetListItemDto[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const containerRef = useRef<HTMLDivElement>(null)
  const searchRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    let cancelled = false
    setIsLoading(true)
    setError(null)
    assetApi.getWebAssetsByWeb(clientCompanyId, workspaceId, campaignId, webId).then(response => {
      if (cancelled) return
      if (response.error || !response.data) {
        setError(response.error || 'Failed to load email assets')
        setIsLoading(false)
        return
      }
      setAssets(
        response.data
          .filter(a => a.Type === EMAIL_COPY_TYPE && a.Status !== DRAFT_STATUS)
          .sort((a, b) => {
            const aRank = a.Status === SIGNED_OFF_STATUS ? 0 : 1
            const bRank = b.Status === SIGNED_OFF_STATUS ? 0 : 1
            return aRank - bRank || a.Name.localeCompare(b.Name)
          })
      )
      setIsLoading(false)
    })
    return () => {
      cancelled = true
    }
  }, [clientCompanyId, workspaceId, campaignId, webId])

  // Close on outside click / Esc; focus the search box when opening.
  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    searchRef.current?.focus()
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  if (isLoading) {
    return <div className="h-10 w-full rounded-lg bg-slate-100 dark:bg-slate-800 animate-pulse" />
  }

  if (error) {
    return <div className="text-sm text-red-600 dark:text-red-400">Could not load email assets: {error}</div>
  }

  if (assets.length === 0) {
    return (
      <div className="text-sm text-slate-600 dark:text-slate-400">
        No email assets for this Web yet.{' '}
        {onNavigateToAssets && (
          <button
            type="button"
            onClick={onNavigateToAssets}
            className="font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 underline decoration-dotted underline-offset-4"
          >
            Create one in Assets →
          </button>
        )}
      </div>
    )
  }

  const selected = selectedAssetId ? assets.find(a => a.Id === selectedAssetId) ?? null : null
  const needle = query.trim().toLowerCase()
  const filtered = needle ? assets.filter(a => lowerSafe(a.Name).includes(needle)) : assets

  const handlePick = (asset: WebAssetListItemDto) => {
    onSelect(asset)
    setOpen(false)
    setQuery('')
  }

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className="w-full flex items-center justify-between gap-2 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2 text-sm text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
      >
        {selected ? (
          <span className="min-w-0 flex items-center gap-2">
            <StatusDot status={selected.Status} />
            <span className="truncate text-slate-900 dark:text-slate-100">{selected.Name}</span>
            <span className="text-xs text-slate-500 dark:text-slate-400 whitespace-nowrap">{selected.Status}</span>
          </span>
        ) : (
          <span className="text-slate-500 dark:text-slate-400">Select an email…</span>
        )}
        <ChevronDown className={'w-4 h-4 text-slate-400 shrink-0 transition-transform ' + (open ? 'rotate-180' : '')} />
      </button>

      {selected && !open && (
        <button
          type="button"
          onClick={onClear}
          className="mt-1 text-xs text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 underline decoration-dotted underline-offset-4"
        >
          Clear selection
        </button>
      )}

      {open && (
        <div className="absolute z-50 mt-1 w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-lg">
          {assets.length > 6 && (
            <div className="relative border-b border-slate-100 dark:border-slate-700/60">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
              <input
                ref={searchRef}
                type="text"
                value={query}
                onInput={e => setQuery((e.target as HTMLInputElement).value)}
                placeholder="Search emails…"
                className="w-full pl-9 pr-3 py-2 text-sm bg-transparent text-slate-900 dark:text-slate-100 focus:outline-none"
              />
            </div>
          )}
          <ul className="max-h-64 overflow-auto py-1" role="listbox">
            {filtered.length === 0 && (
              <li className="px-3 py-2 text-sm text-slate-500 dark:text-slate-400">No matches.</li>
            )}
            {filtered.map(asset => {
              const isSelected = asset.Id === selectedAssetId
              const isUsed = usedAssetIds?.has(asset.Id)
              return (
                <li key={asset.Id} role="presentation">
                  <button
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    onClick={() => handlePick(asset)}
                    className={
                      'w-full flex items-center gap-2 px-3 py-2 text-left text-sm hover:bg-slate-100 dark:hover:bg-slate-700/60 ' +
                      (isSelected ? 'bg-slate-100 dark:bg-slate-700/60' : '')
                    }
                  >
                    <StatusDot status={asset.Status} />
                    <span className="min-w-0 flex-1 truncate text-slate-900 dark:text-slate-100">{asset.Name}</span>
                    {isUsed && <span className="text-[10px] uppercase tracking-wide text-slate-400 shrink-0">Used</span>}
                    <span className="text-xs text-slate-500 dark:text-slate-400 whitespace-nowrap shrink-0">{asset.Status}</span>
                    {isSelected && <Check className="w-4 h-4 text-primary-500 shrink-0" />}
                  </button>
                </li>
              )
            })}
          </ul>
        </div>
      )}
    </div>
  )
}
